import { BaseComponent } from '../base-component/base-component';
import { html, css, TemplateResult } from 'lit';
import { connect } from 'pwa-helpers';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { smallSpinner } from '../../common/constants/api-constants';
import { customElement, query, state, property } from 'lit/decorators.js';
import { nothing } from 'lit-html';
import { MessageType } from '../alert/global-message.interface';
import { getReturnEligibleThrough, getReturnThrough } from './utils/return.util';
import { OrderHistory } from '../order-history/order-history.interface';
import { getPriceWithCurrency } from '../../common/utils/cart-util';
import { returnOrder } from './thunks/order-return.thunk';
import { getOrderReturnData } from './store/return.selector';
import { mapOrderToReturnRequestParams } from './mappings/return.mapping';
import {
  ReturnAttachmentUrlRequestParams,
  ReturnCaseAttachment,
  ReturnReason,
  ReturnType
} from './interfaces/return-request.interface';
import { ReturnCondition, ReturnStatus } from './interfaces/return-info.interface';
import { getPresignedUrl } from './apis/return.api';
import emit from '../../common/events';
import { getAppFlow, getUserActiveTab } from '../user-profile/utils/user-profile.util';
import { getAnalyticsSafeData, selectUser } from '../user-profile/user-profile.selectors';
import { getOrderTypeFromOrderDetails, getProductTypeBU } from '../../common/utils/analytics-util';
import { Modal } from 'bootstrap';
import { formatDateDefault } from '../../common/utils/util';
import { handleModalFocusTrap, ariaLabelWithNewTab } from '../../common/utils/a11y-util';

const returnReasonOptions = [
  {
    id: "class-canceled",
    label: "Class cancelled, class dropped, or instructor does not require product.",
    option: ReturnReason.CLASS_CANCELLED,
  },
  {
    id: "wrong-email",
    label: "I purchased using the wrong e-mail address.",
    option: ReturnReason.PURCHASED_WRONG_EMAIL,
  },
  {
    id: "wrong-or-duplicated-product",
    label: "I purchased the wrong product or made a duplicate purchase elsewhere.",
    option: ReturnReason.PURCHASED_WRONG_PRODUCT,
  },
  {
    id: "technical-issue",
    label: "Unresolved technical issue.",
    option: ReturnReason.UNRESOLVED_TECHNICAL_ISSUE,
  }
];

enum Mode {
  REQUEST_RETURN, VIEW_STATUS, VIEW_MESSAGE
}

enum ModalCloseType {
  CLOSE_BUTTON = 'close-button',
  X_BUTTON = 'x-button',
  ESCAPE_KEY = 'escape-key',
  BACKDROP_CLICK = 'backdrop-click'
}

const returnPolicyLink = 'https://www.mheducation.com/return-policy.html';

const globalSitesLink = 'https://www.mheducation.com/about-us/locations/global-sites.html';

const surveyLink = 'https://educationinsight.co1.qualtrics.com/jfe/form/SV_egJEsZL5Vf1xDAW';

/**
 * Button and modal for order return
 */
@customElement('mhecomm-order-return')
export class OrderReturn extends connect(store)(BaseComponent) {

  static styles = [
    css`
      .return-details {
        line-height: 24px;
      }
    `,
  ];

  @property({type: Object})
  order?: OrderHistory;

  @state()
  private isRequesting = false;

  @query('#file-input')
  private fileInputElement?: HTMLInputElement;

  @state()
  private returnReason?: ReturnReason;

  @state()
  private isInvalid = false;

  @state()
  private isModalVisible = false;

  private triggerButton?: HTMLButtonElement;

  private modal?: Modal;
  private modalCloseType: ModalCloseType | undefined;
  private returnStatus?: ReturnStatus;
  private caseNumber?: string;
  private isReturnSuccessfullySubmitted = false;
  private returnType?: ReturnType;
  private mode?: Mode;
  private attachment: ReturnCaseAttachment = {file_name: ''}

  private closeModalAnalyticsListener = this._closeModalAnalyticsListener.bind(this);
  private backdropClickListener = this._backdropClickListener.bind(this);
  private keydownListener = this._handleKeydown.bind(this);
  private shownModalListener = this._onModalShown.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.returnType = this.order?.returnInfo?.returnType ??
      (this.order?.returnInfo?.returnCondition === ReturnCondition.RETURN_CASE ?
        ReturnType.SFDC_CASE : ReturnType.RETURN);
    this.returnStatus = this.order?.returnInfo?.status ?? ReturnStatus.NON_RETURNABLE;
    this.mode = this._getMode();
    this.returnReason = this.order?.returnInfo?.status === ReturnStatus.RETURNABLE ? undefined : this.order?.returnInfo?.returnReason;
    this.caseNumber = this.order?.returnInfo?.caseNumber;
  }

  private _getMode(): Mode {
    switch (this.returnStatus) {
      case ReturnStatus.RETURNABLE:
        return Mode.REQUEST_RETURN;
      case ReturnStatus.PENDING:
      case ReturnStatus.APPROVED:
      case ReturnStatus.REJECTED:
        return Mode.VIEW_STATUS;
      default:
        if (this.order?.returnInfo?.returnCondition === ReturnCondition.RETURN_CASE) {
          return Mode.REQUEST_RETURN;
        }
        return Mode.VIEW_MESSAGE;
    }
  }

  private _isRequestReturnMode() {
    return this.mode === Mode.REQUEST_RETURN;
  }

  private _isViewStatusMode() {
    return this.mode === Mode.VIEW_STATUS;
  }

  private _isViewMessageMode() {
    return this.mode === Mode.VIEW_MESSAGE;
  }

  updated() {
    this.modal = this.bsInit('#order-return-modal', window.bootstrap?.Modal)?.instance;
    this._registerModalListeners();
  }

  private _registerModalListeners() {
    const modalEl = this.shadowRoot?.getElementById("order-return-modal");
    modalEl?.removeEventListener('shown.bs.modal', this.shownModalListener);
    modalEl?.addEventListener('shown.bs.modal', this.shownModalListener);
    modalEl?.removeEventListener('hide.bs.modal', this.backdropClickListener);
    modalEl?.addEventListener('hide.bs.modal', this.backdropClickListener);
    modalEl?.removeEventListener('keydown', this.keydownListener);
    modalEl?.addEventListener('keydown', this.keydownListener);
    modalEl?.removeEventListener('hidden.bs.modal', this.closeModalAnalyticsListener);
    modalEl?.addEventListener('hidden.bs.modal', this.closeModalAnalyticsListener);
    modalEl?.removeEventListener('shown.bs.modal', this.shownModalListener);
    modalEl?.addEventListener('shown.bs.modal', this.shownModalListener);
  }

  private _onModalShown() {
    const closeBtn = this.shadowRoot?.querySelector<HTMLButtonElement>('#order-return-modal .btn-close');
    requestAnimationFrame(() => closeBtn?.focus());
  }

  private _handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.modalCloseType = ModalCloseType.ESCAPE_KEY;
    }
    handleModalFocusTrap(event, this.shadowRoot!, event.currentTarget as HTMLElement);
  }

  private _backdropClickListener() {
    if (this.modalCloseType === undefined) {
      this.modalCloseType = ModalCloseType.BACKDROP_CLICK;
    }
  }

  private _shownModalFocusListener() {
    const closeButton = this.shadowRoot?.querySelector<HTMLButtonElement>('.btn-close');
    closeButton?.focus();
  }

  private _closeModalAnalyticsListener() {
    if (this.modalCloseType !== undefined) {
      this._fireCloseModalAnalytics(this.modalCloseType);
      this.modalCloseType = undefined;
    }
    requestAnimationFrame(() => this.triggerButton?.focus());
  }

  renderComp() {
    return html`
      <button
        type="button"
        class="btn btn-sm border-0 link-primary m-0 p-0"
        aria-haspopup="dialog"
        aria-controls="order-return-modal"
        aria-label="${this._getOpenModalButtonText()} for ${this.order?.webOrderNo}"
        data-automation-id="btn-request-return-${this.order?.orderId}"
        @click=${this._openModal}>
        ${this._getOpenModalButtonText()}
      </button>
      &nbsp;|&nbsp;
      ${this._getReturnPolicyLink()}
      <div
        class="modal fade"
        id="order-return-modal"
        tabindex="-1"
        aria-hidden="${this.isModalVisible ? 'false' : 'true'}"
        aria-modal="${this.isModalVisible ? 'true' : 'false'}"
        role="${this.isModalVisible ? 'dialog' : nothing}"
        aria-labelledby="order-return-header">
        <div class="modal-dialog modal-dialog-centered modal-lg modal-fullscreen-md-down text-start" role="document">
          <div class="modal-content">
            <div class="modal-header align-items-start">
              <h2 class="h5 modal-title" id="order-return-header">
                ${this._getModalHeader()}
              </h2>
              <button
                type="button"
                class="btn-close"
                @click=${() => this._closeModal(ModalCloseType.X_BUTTON)}
                data-bs-dismiss="modal"
                aria-label="close">
              </button>
            </div>
            <div class="modal-body">
              ${this.order !== undefined ? html`${
                this._getModalBody(this.order)
              }` : nothing}
              <div class="row mt-3">
                ${this._getReturnPolicyLink()}
              </div>
            </div>
            <div class="modal-footer">
              <button
                id="modal-secondary-button"
                type="button"
                class="btn btn-primary w-100 w-md-auto"
                aria-label="${this._isRequestReturnMode() ? 'Submit' : 'Close'}"
                ?disabled=${this.isRequesting}
                @click=${() => this._onModalButtonClick()}>
                ${this._getModalActionButtonText()} ${this.isRequesting ? smallSpinner() : nothing}
              </button>
            </div>
          </div>
        </div>
      </div>`;
  }

  private _getModalBody(order: OrderHistory): TemplateResult | typeof nothing {
    switch (this.mode) {
      case Mode.REQUEST_RETURN:
        return this._getRequestReturnBody(order);
      case Mode.VIEW_STATUS:
        return this._getViewStatusBody(order);
      case Mode.VIEW_MESSAGE:
        return this._getViewMessageBody(order);
      default:
        return nothing;
    }
  }

  private _getRequestReturnBody(order: OrderHistory) {
    return html`
      <div class="row">
        <div class="col">
          <mhecomm-alert
            type="${this._getAlertType()}"
            ?dismissible="${false}"
            message="${this._getAlertMessage()}"
          ></mhecomm-alert>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="mb-3">
            <span class="fw-bold">Return Eligible Through:</span>
            ${formatDateDefault(getReturnEligibleThrough(order))}
          </div>
        </div>
      </div>
      ${this.returnType === ReturnType.SFDC_CASE ? html`
        <div class="row">
          <div class="col">
            <div class="mb-3">
              <span>Reason for return may require proof. Each return case is reviewed before potential approval. <a
                @click=${this._firePolicyLinkAnalytics} href="${returnPolicyLink}" aria-label="${ariaLabelWithNewTab('Return policy')}" target="_blank">Return Policy</a></span>
            </div>
          </div>
        </div>
      ` : nothing}
      ${this._getOrderDetailsCards(order)}
      <fieldset class="row">
        <legend class="mb-0 mt-3 fs-6 fw-bold">Reason for Return</legend>
        <div class="mt-2">
          ${returnReasonOptions.map(
            (displayOption) => {
              return html`
                <div class="form-check mt-2">
                  <input
                    class="form-check-input ${this._isReasonInvalid() ? 'is-invalid' : ''}"
                    type="radio"
                    name="return-options"
                    id="return-options-${displayOption.id}"
                    .checked=${this.returnReason === displayOption.option}
                    @change=${() => this.returnReason = displayOption.option}>
                  <label class="form-check-label" for="return-options-${displayOption.id}">
                    ${displayOption.label}
                  </label>
                  ${ReturnReason.CLASS_CANCELLED === displayOption.option && this.returnType === ReturnType.SFDC_CASE ? html`
                    <div class="mt-1">
                      <label class="form-label" for="file-input">
                        <span class="fw-bold">Screenshot</span>
                      </label>
                      <span class="fs-small text-gray"
                            id="file-details">Max file size 3MB (JPG, PNG, PDF)</span>
                      <input class="form-control ${this._isFileInvalid() ? 'is-invalid' : ''}" type="file"
                             id="file-input"
                             aria-describedby="file-details"
                             ?disabled=${this.returnReason !== ReturnReason.CLASS_CANCELLED}
                             @change=${this._handleFileInputChange}>
                      ${this._isFileInvalid() ? html`
                        <div class="mt-1 invalid-feedback d-block" aria-live="assertive">
                          <uxt-icon icon="exclamation-triangle"></uxt-icon>
                          <span class="visually-hidden">Error&nbsp;</span>&nbsp;The required Screenshot must be less
                          than 3MB and of JPG, PNG, or PDF format
                        </div>
                      ` : nothing}
                    </div>
                  ` : nothing}
                </div>
              `
            }
          )}
          ${this._isReasonInvalid() ? html`
            <div class="mt-1 invalid-feedback d-block" aria-live="assertive">
              <uxt-icon icon="exclamation-triangle"></uxt-icon>
              <span class="visually-hidden">Error&nbsp;</span>&nbsp;Reason for Return is required
            </div>
          ` : nothing}
        </div>
      </fieldset>`;
  }

  private _getViewStatusBody(order: OrderHistory) {
    return html`
      <div class="row">
        <div class="col">
          <mhecomm-alert
            type="${this._getAlertType()}"
            ?dismissible="${false}"
            message="${this._getAlertMessage()}"
          ></mhecomm-alert>
        </div>
      </div>
      ${this._getOrderDetailsCards(order)}
      ${this.caseNumber ? html`
        <div class="row">
          <div class="col">
            <div class="mt-3">
              <span class="fw-bold">Case Number:</span> ${this.caseNumber}
            </div>
          </div>
        </div>
      ` : html`
        <div class="row">
          <div class="col">
            <div class="mt-3">
              <span>You will receive a case number by email.</span>
            </div>
          </div>
        </div>
      `}
      <div class="row">
        <div class="col">
          <div class="mt-3">
            <span class="fw-bold">Reason for Return:</span> ${(this._getReturnReason())}
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="mt-3">
            <span class="return-details">${this._getReturnDetails()}</span>
          </div>
        </div>
      </div>
      ${this.isReturnSuccessfullySubmitted ? html`
        <div class="row">
          <div class="col">
            <div class="my-3">
              <span>${this._getSurveyMessage()}</span>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="my-3 fs-large">
              <a href="${surveyLink}" aria-label="${ariaLabelWithNewTab('Help us improve')}" target="_blank" @click=${this._fireHelpUsImproveAnalytics}>
                <uxt-icon icon="clipboard-data" class="me-2"></uxt-icon>
                Help us Improve</a>
            </div>
          </div>
        </div>
      ` : nothing}`;
  }

  private _fireHelpUsImproveAnalytics() {
    emit(this._buildAnalyticsEvent('return-feedback-click', this._getReturnInfoAnalytics()));
  }

  private _getViewMessageBody(order: OrderHistory) {
    return html`
      ${this._getNonReturnableAlert(order)}
      ${this._getOrderDetailsCards(order)}
      ${this._getNonReturnableMessage(order)}
    `;
  }

  private _getNonReturnableAlert(order: OrderHistory): TemplateResult | typeof nothing {
    if (this._shouldShowAlert(order)) {
      return html`
        <mhecomm-alert
          type="${MessageType.warning}"
          ?dismissible="${false}"
          message="A return request can only be approved for an entire order."
        ></mhecomm-alert>
      `;
    }
    return nothing;
  }

  private _shouldShowAlert(order: OrderHistory) {
    const condition = order?.returnInfo?.returnCondition;

    const alertConditions = [
      ReturnCondition.PRINT_WITHOUT_ADDRESS,
      ReturnCondition.PRINT_WITH_ADDRESS,
      ReturnCondition.PRINT_WITHOUT_ADDRESS_12_MONTHS
    ];

    return condition && alertConditions.includes(condition);
  }

  private _getNonReturnableMessage(order: OrderHistory) {
    switch (order.returnInfo?.returnCondition) {
      case ReturnCondition.CONTACT_SUPPORT_1:
        return this._getContactSupport1Message();
      case ReturnCondition.CONTACT_SUPPORT_2:
        return this._getContactSupport2Message();
      case ReturnCondition.CONTACT_SUPPORT_INTL:
        return this._getContactSupportIntlMessage();
      case ReturnCondition.CONTACT_SUPPORT_FIRST_AID_FORWARD:
        return this._getContactSupportFirstAidForwardMessage();
      case ReturnCondition.CONTACT_SUPPORT_BOARDS_AND_BEYOND:
        return this._getContactSupportBoardsandBeyondMessage();
      case ReturnCondition.CONTACT_SUPPORT_PK12_CUSTOM_BOOKSTORE:
        return this._getContactSupportPK12CustomBookstoreMessage();
      case ReturnCondition.WPR:
        return this._getWPRMessage();
      case ReturnCondition.PRINT_WITHOUT_ADDRESS_12_MONTHS:
      case ReturnCondition.PRINT_WITHOUT_ADDRESS:
      case ReturnCondition.PRINT_WITH_ADDRESS:
        return this._getPrintMessage(order.returnInfo.returnCondition);
      default:
        return this._getContactSupport1Message();
    }
  }

  private _getContactSupport1Message() {
    return this._getContactSupportMessage('contact-support-1', 'hep_customer-service@mheducation.com', '1-800-338-3987');
  }

  private _getContactSupport2Message() {
    return this._getContactSupportMessage('contact-support-2', 'subscriptionmanagement@mheducation.com', '1-800-598-4077');
  }

  private _getContactSupportPK12CustomBookstoreMessage() {
    return this._getContactSupportMessage('contact-support-pk12-custom-bookstore', 'hep_customer-service@mheducation.com', '1-800-338-3987');
  }

  private _getContactSupportBoardsandBeyondMessage() {
    return this._getContactSupportMessage('contact-support-boards-and-beyond', 'bbcustomerservice@mheducation.com');
  }

  private _getContactSupportFirstAidForwardMessage() {
    return this._getContactSupportMessage('contact-support-first-aid-forward', 'onlinecustomer_service@mheducation.com');
  }

  private _getContactSupportMessage(contactType: string, email: string, phone?: string) {
    return html`
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>
              We apologize for the inconvenience, but we're unable to process your request through the automated system. 
              Please submit your name, web order number, and reason for return via email to 
              <a @click=${() => this._fireMailContactAnalytics(contactType)}
                href="mailto:${email}">${email}</a>
              ${phone ? html`
                or call ${phone}.` : nothing}
            </span>
          </div>
        </div>
      </div>
    `;
  }

  private _getContactSupportIntlMessage() {
    return html`
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>We can’t process your return via the automated system.</span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="return-details">
            <span class="fw-bold">For international returns, email your name, order number, and reason to: </span>
            <a @click=${() => this._fireMailContactAnalytics('contact-support-intl')}
               href="mailto:international_cs@mheducation.com">international_cs@mheducation.com</a>.
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>Additional support is available on our <a href="${globalSitesLink}" @click=${this._fireGlobalSitesLinkAnalytics} aria-label="${ariaLabelWithNewTab('Global sites')}" target="_blank">Global Sites</a> page.</span>
          </div>
        </div>
      </div>
    `;
  }

  private _getWPRMessage() {
    return html`
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>
              Unfortunately, this product type isn’t supported by our automated system. 
              Visit <a href="https://mh.my.site.com/CSOM/s/website-product-return" aria-label="${ariaLabelWithNewTab('Customer service')}" target="_blank">Customer Service</a> to request a return.
            </span>
          </div>
        </div>
      </div>
    `;
  }

  private _getPrintMessage(condition: ReturnCondition) {
    return html`
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>
              Your order contains a print product, which may be returned within 
              ${condition === ReturnCondition.PRINT_WITHOUT_ADDRESS_12_MONTHS ? '12 months' : '21 days'} 
              of the date billed. 
              The print product must be received by McGraw Hill within this timeframe to be eligible for a return.
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span>
              If you purchased a digital product, your digital access will be credited and removed from your account 
              once the print product is received and processed for credit. 
              To ensure proper crediting, include a copy of your McGraw Hill invoice or packing list with your return.
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="mt-3 return-details">
            <span class="fw-bold">${condition === ReturnCondition.PRINT_WITH_ADDRESS ?
              'Return to Address' :
              'Send return to the address on your invoice'
            }</span><br/>
            ${condition === ReturnCondition.PRINT_WITH_ADDRESS ? html`
              <span>
                McGraw Hill LLC <br/>
                860 Taylor Station Road <br/>
                Blacklick, OH 43004 <br/>
              </span>
            ` : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private _handleFileInputChange(event: Event) {
    this.attachment.file_name = '';
    this.attachment.extension = undefined;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const validExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
      const maxSizeInBytes = 3 * 1024 * 1024; // 3MB

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        input.value = ''; // Clear the input
        this.isInvalid = true;
        return;
      }

      if (file.size > maxSizeInBytes) {
        input.value = ''; // Clear the input
        this.isInvalid = true;
        return;
      }
      this.attachment.file_name = file.name;
      this.attachment.extension = fileExtension;
      this.isInvalid = false;
    }
  }

  private async uploadFile() {
    if (!this.attachment.file_name || !this.attachment.extension || !this.fileInputElement) {
      return;
    }

    try {
      const file = this.fileInputElement.files![0];
      const params = this._getPresignedUrlRequestParams();
      const presignedUrl = await getPresignedUrl(params);
      const response = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type || "application/octet-stream"
        }
      });

      if (response.ok) {
        console.log("File uploaded successfully!");
      } else {
        //TODO: return error on UI?
        console.error("Upload failed:", await response.text());
      }

      this.attachment.url = presignedUrl;
    } catch (error) {
      console.error("Error:", error);
    }
  }

  private _getOpenModalButtonText() {
    return this._isRequestReturnMode() || this._isViewMessageMode() ? 'Request Return' : 'Return Status';
  }

  private _getModalActionButtonText() {
    if (this._isRequestReturnMode()) {
      return 'Submit';
    }
    return 'Close';
  }

  private _getReturnReason() {
    return returnReasonOptions.find(option => option.option === this.returnReason)?.label;
  }

  private _getReturnDetails() {
    switch (this.returnStatus) {
      case ReturnStatus.PENDING:
        return `Your return is pending review and should resolve in 
        ${this.returnType === ReturnType.SFDC_CASE ? '3–7' : '1–2'} 
        business days. You will have access to this product until the return is approved. Thank you for your patience.`;
      case ReturnStatus.APPROVED:
        return 'Depending upon your financial institution, it can take up to 10 days to see your refund.';
      case ReturnStatus.REJECTED:
        return html`
          Return request has been reviewed and deemed ineligible for return.
          You may contact <a @click=${() => this._fireMailContactAnalytics('denied-contact')}
            href="mailto:hep_customer-service@mheducation.com">hep_customer-service@mheducation.com</a> or see our
          <a href="${returnPolicyLink}" @click=${this._firePolicyLinkAnalytics} aria-label="${ariaLabelWithNewTab('Return policy')}" target="_blank">Return Policy</a>
          for additional information.`;
      default:
        return '';
    }
  }

  private _fireMailContactAnalytics(contactType: string) {
    emit(this._buildAnalyticsEvent('return-mail-' + contactType, this._getReturnInfoAnalytics()));
  }

  private _getSurveyMessage() {
    return html`Would you be willing to complete a
    <a target="_blank"
       aria-label="${ariaLabelWithNewTab('Short survey')}"
       href="${surveyLink}"
       @click=${this._fireSurveyAnalytics}>
      short survey
    </a>
    ? Your feedback is greatly appreciated.`;
  }

  private _fireSurveyAnalytics() {
    emit(this._buildAnalyticsEvent('return-survey-click', this._getReturnInfoAnalytics()));
  }

  private _getAlertType() {
    switch (this.returnStatus) {
      case ReturnStatus.PENDING:
        return MessageType.info;
      case ReturnStatus.APPROVED:
        return MessageType.success;
      case ReturnStatus.REJECTED:
        return MessageType.danger;
      default:
        return this.isInvalid ? MessageType.danger : MessageType.warning;
    }
  }

  private _getAlertMessage() {
    switch (this.returnStatus) {
      case ReturnStatus.PENDING:
        return 'Return Pending.';
      case ReturnStatus.APPROVED:
        return 'Return Approved.';
      case ReturnStatus.REJECTED:
        return 'Return Denied.';
      default:
        if (this.isInvalid) {
          return 'Correct all indicated errors.';
        }
        return 'Entire orders have to be returned together. You will retain access to digital items until return is processed.';
    }
  }

  private _getModalHeader() {
    if (this._isRequestReturnMode() || this._isViewMessageMode()) {
      return 'Request Return';
    }
    return 'Return Status';
  }


  private _openModal(event: Event) {
    this.triggerButton = event.currentTarget as HTMLButtonElement;
    this._fireOpenModalAnalytics();
    this.modal?.show();
    this.isModalVisible = true;
  }

  private _fireOpenModalAnalytics() {
    if (this._isRequestReturnMode() || this._isViewMessageMode()) {
      emit(this._buildAnalyticsEvent('request-return-modal-open', {
        returnEligibleThrough: getReturnEligibleThrough(this.order!).substring(0, 10),
        returnThrough: getReturnThrough(this.order!).substring(0, 10),
        ...getOrderTypeFromOrderDetails(this.order!)
      }));
    } else if (this._isViewStatusMode()) {
      emit(this._buildAnalyticsEvent('return-status-modal-open',
        this._getReturnInfoAnalytics()));
    }
  }

  private _fireCloseModalAnalytics(closeModalType: ModalCloseType) {
    emit(this._buildAnalyticsEvent('return-modal-close', {
      closeModalType: closeModalType,
      ...this._getReturnInfoAnalytics()
    }));
  }

  private _fireSubmitModalAnalytics() {
    emit(this._buildAnalyticsEvent('return-modal-submit',
      {
        returnReason: this._getReturnReasonAnalytics(),
        returnType: this.returnType,
        ...getOrderTypeFromOrderDetails(this.order!)
      }));
  }

  private _getReturnInfoAnalytics() {
    return {
      returnType: this.returnType,
      ...getOrderTypeFromOrderDetails(this.order!),
      returnEligibleThrough: getReturnEligibleThrough(this.order!).substring(0, 10),
      returnThrough: getReturnThrough(this.order!).substring(0, 10),
      returnDateRequested: this.order?.returnInfo?.dateRequested,
      returnCaseNumber: this.order?.returnInfo?.caseNumber,
      returnReason: this._getReturnReasonAnalytics(),
      returnStatus: this.returnStatus,
    }
  }

  private _getReturnReasonAnalytics() {
    return {
      code: this.returnReason,
      description: this._getReturnReason(),
    };
  }

  private _buildAnalyticsEvent(eventName: string, details: any) {
    if (this.order) {
      return {
        element: this,
        name: eventName,
        detail: {
          eventName: eventName,
          isAutoReturnable: !this._isViewMessageMode(),
          ...details,
          appFlow: getAppFlow(),
          user: getAnalyticsSafeData(selectUser(store.getState() as StoreState), true),
          orderNumber: this.order.webOrderNo,
          orderedDate: this.order.orderedDate.substring(0, 10),
          overAllOrderStatus: this.order.overAllOrderStatus.toString(),
          totalPrice: this.order.totalPrice,
          isEdoms: !!this.order.metaOfferId,
          myAccountSection: getUserActiveTab(),
          ...getProductTypeBU({programIds: this.order.userOrderDetails.map((product) => product.programSourceId ?? '')}),
        },
      };
    }
    return {
      element: this,
      name: eventName,
      detail: details
    };
  }

  private _closeModal(closeType: ModalCloseType) {
    this.modalCloseType = closeType;
    this.modal?.hide();
    if (this.isReturnSuccessfullySubmitted) {
      this._fireEvent();
    }
    this.isReturnSuccessfullySubmitted = false;
    this.isModalVisible = false;
  }

  private async _onModalButtonClick() {
    if (this._isRequestReturnMode()) {
      if (this._shouldHaveAttachment()) {
        await this.uploadFile();
      }
      this._openReturn();
      this._fireSubmitModalAnalytics();
    } else {
      this._closeModal(ModalCloseType.CLOSE_BUTTON);
    }
  }

  private _getPresignedUrlRequestParams() {
    const params: ReturnAttachmentUrlRequestParams = {
      orderNumber: this.order!.webOrderNo,
      returnAttachmentUrlRequest: {
        returnCaseAttachment: this.attachment
      }
    };
    return params;
  }

  private _openReturn() {
    if (this.order) {
      if (this._isValidated()) {
        this.isRequesting = true;
        store.dispatch(returnOrder(mapOrderToReturnRequestParams(this.order, this.returnReason!, this.returnType!, this._getReturnCaseAttachment())))
          .then(() => this._processResponse())
          .catch((error) => {
            console.log('Error:', error);
            this.isRequesting = false;
          });
      } else {
        this.isInvalid = true;
      }
    }
  }

  private _fireEvent() {
    const event = new CustomEvent('mhecomm-order-returned', {bubbles: true});
    try {
      this.dispatchEvent(event);
    } catch (exception) {
      console.log('Error while firing event:', exception);
    }
  }

  private _getReturnCaseAttachment() {
    return this._shouldHaveAttachment() ? this.attachment : undefined;
  }

  private _isValidated() {
    return this.returnReason !== undefined && !this._requiredAttachmentIsMissing();
  }

  private _isReasonInvalid() {
    return this.isInvalid && !this.returnReason;
  }

  private _isFileInvalid() {
    return this.isInvalid && this._requiredAttachmentIsMissing();
  }

  private _requiredAttachmentIsMissing() {
    return this._shouldHaveAttachment() && !this.attachment.file_name
  }

  private _processResponse() {
    this.isRequesting = false;
    this.isReturnSuccessfullySubmitted = true;
    const returnResponse = getOrderReturnData(store.getState() as StoreState);
    if (returnResponse) {
      this.returnStatus = ReturnStatus.PENDING;
      this.mode = Mode.VIEW_STATUS;
      this.caseNumber = returnResponse.caseNumber;
    } else {
      console.log('Error?', returnResponse)
    }
  }

  private _getReturnPolicyLink() {
    return html`<a class="fs-small" @click=${this._firePolicyLinkAnalytics} href="${returnPolicyLink}" aria-label="${ariaLabelWithNewTab(`Return policy for ${this.order?.webOrderNo}`)}" target="_blank">Return Policy</a>`;
  }

  private _firePolicyLinkAnalytics() {
    emit(this._buildAnalyticsEvent('return-policy-link-click', this._getReturnInfoAnalytics()));
  }

  private _fireGlobalSitesLinkAnalytics() {
    emit(this._buildAnalyticsEvent('return-global-sites-link-click', this._getReturnInfoAnalytics()));
  }

  private _getOrderDetailsCards(order: OrderHistory) {
    return html`
      <div class="row fs-small">
        <div class="col-12 d-md-none">
          <dl class="card bg-light-blue border-0 rounded-3 text-start p-2 mb-0">
            <div class="d-flex">
              <dt class="fw-bold mx-2">Order Number</dt>
              <dd class="mb-0">${order.webOrderNo}</dd>
            </div>
            <div class="d-flex mt-2">
              <dt class="fw-bold mx-2">Order Placed</dt>
              <dd class="mb-0">${new Date(order.orderedDate).toLocaleDateString('en-US')}</dd>
            </div>
            <div class="d-flex mt-2">
              <dt class="fw-bold mx-2">Order Total</dt>
              <dd class="mb-0">${getPriceWithCurrency(order.currencyCode, order.totalPrice)}</dd>
            </div>
          </dl>
        </div>
        <div class="col d-none d-md-block">
          <dl class="card bg-light-blue border-0 rounded-3 text-center p-1 mb-0">
            <dt class="fw-bold m-1">Order Number</dt>
            <dd class="m-1">${order.webOrderNo}</dd>
          </dl>
        </div>
        <div class="col d-none d-md-block">
          <dl class="card bg-light-blue border-0 rounded-3 text-center p-1 mb-0">
            <dt class="fw-bold m-1">Order Placed</dt>
            <dd class="m-1">${new Date(order.orderedDate).toLocaleDateString('en-US')}</dd>
          </dl>
        </div>
        <div class="col d-none d-md-block">
          <dl class="card bg-light-blue border-0 rounded-3 text-center p-1 mb-0">
            <dt class="fw-bold m-1">Order Total</dt>
            <dd class="m-1">${getPriceWithCurrency(order.currencyCode, order.totalPrice)}</dd>
          </dl>
        </div>
      </div>
    `;
  }

  private _shouldHaveAttachment() {
    return this.returnType === ReturnType.SFDC_CASE && this.returnReason === ReturnReason.CLASS_CANCELLED;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-order-return': OrderReturn;
  }
}
